<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Group, GroupListItem } from '@/types/group'

import { appConfirm } from '@/composables/useAppConfirm'
import { appTableSortableHeader } from '@/composables/useAppTable'
import GroupFormDialog from '@/pages/groups/GroupFormDialog.vue'
import { groupService } from '@/services/groupService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const { t } = useI18n()
const { adminModeEnabled } = useAdminMode()
const {
  groups,
  loading,
  searchTerm,
  loadGroups,
  deleteGroup: deleteGroupFromList,
} = useGroups({ autoLoad: false })

const showCreateDialog = ref(false)
const selectedGroup = ref<Group | null>(null)

const standaloneGroups = computed(() => groups.value.filter((group) => !group.workspaceId))
const visibilityLabel = (visibility: GroupListItem['visibility']) =>
  t(`enums.visibility.${visibility.toLowerCase()}`)

const filteredGroups = computed(() => {
  if (!searchTerm.value) {
    return standaloneGroups.value
  }

  const search = searchTerm.value.toLowerCase()
  return standaloneGroups.value.filter(
    (group) =>
      group.nameDE.toLowerCase().includes(search) ||
      group.nameEN?.toLowerCase().includes(search) ||
      group.customId.toLowerCase().includes(search),
  )
})
const columns = computed<TableColumn<GroupListItem>[]>(() => [
  {
    accessorKey: 'nameDE',
    header: appTableSortableHeader<GroupListItem>(t('pages.groups.management.columns.name')),
  },
  {
    accessorKey: 'customId',
    header: appTableSortableHeader<GroupListItem>(t('pages.groups.management.columns.customId')),
  },
  {
    accessorKey: 'eventCount',
    header: appTableSortableHeader<GroupListItem>(t('pages.groups.management.columns.events')),
  },
  {
    accessorKey: 'visibility',
    header: appTableSortableHeader<GroupListItem>(t('pages.groups.management.columns.visibility')),
  },
  { id: 'actions', header: t('pages.groups.management.columns.actions') },
])

function openCreateDialog() {
  selectedGroup.value = null
  showCreateDialog.value = true
}

function updateDialogVisible(value: boolean) {
  showCreateDialog.value = value
  if (!value) {
    selectedGroup.value = null
  }
}

async function editGroup(group: GroupListItem) {
  selectedGroup.value = await groupService.getById(group.id)
  showCreateDialog.value = true
}

async function deleteGroup(id: number) {
  try {
    await deleteGroupFromList(id)
  } catch {
    // The composable shows the error toast.
  }
}

async function confirmDelete(group: GroupListItem) {
  const accepted = await appConfirm({
    message: t('pages.groups.management.confirmDeleteMessage', { name: group.nameDE }),
    title: t('common.labels.confirmDeletion'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await deleteGroup(group.id)
}

function onGroupSaved() {
  showCreateDialog.value = false
  selectedGroup.value = null
  loadGroups(adminModeEnabled.value)
}

watch(adminModeEnabled, () => {
  loadGroups(adminModeEnabled.value)
})

onMounted(() => {
  loadGroups(adminModeEnabled.value)
})
</script>

<template>
  <div class="groups-page">
    <div class="mb-6 flex items-center justify-between gap-4">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ t('pages.groups.management.title') }}
        </h2>
        <p class="mt-1 text-gray-600 dark:text-gray-300">
          {{ t('pages.groups.management.subtitle') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-plus"
        :label="t('pages.groups.management.actions.create')"
        color="neutral"
        @click="openCreateDialog"
      />
    </div>

    <UCard class="mb-6">
      <template #default>
        <AppIconField>
          <AppInputIcon name="i-lucide-search" />
          <UInput
            v-model="searchTerm"
            :placeholder="t('pages.groups.management.searchPlaceholder')"
            class="w-full"
          />
        </AppIconField>
      </template>
    </UCard>

    <UCard>
      <template #default>
        <AppDataTableShell
          :data="filteredGroups"
          :columns="columns"
          :loading="loading"
          paginate
          :page-size="10"
        >
          <template #empty>
            <div class="py-8 text-center">
              <UIcon name="i-lucide-users" class="mb-4 text-4xl text-gray-400 dark:text-gray-500" />
              <h3 class="mb-2 text-lg font-semibold text-gray-600 dark:text-gray-300">
                {{ t('pages.groups.management.empty.title') }}
              </h3>
              <p class="text-gray-500 dark:text-gray-400">
                {{ t('pages.groups.management.empty.subtitle') }}
              </p>
            </div>
          </template>

          <template #nameDE-cell="{ row }">
            <div>
              <div class="font-semibold">
                {{ row.original.nameDE }}
              </div>
              <div v-if="row.original.nameEN" class="text-sm text-gray-500 dark:text-gray-400">
                {{ row.original.nameEN }}
              </div>
            </div>
          </template>

          <template #customId-cell="{ row }">
            <code class="rounded bg-gray-100 px-2 py-1 text-sm dark:bg-gray-800">
              {{ row.original.customId }}
            </code>
          </template>

          <template #eventCount-cell="{ row }">
            <UBadge :label="row.original.eventCount" color="info" />
          </template>

          <template #visibility-cell="{ row }">
            <UBadge :label="visibilityLabel(row.original.visibility)" color="neutral" />
          </template>

          <template #actions-cell="{ row }">
            <div class="flex gap-2">
              <UButton
                v-app-tooltip="t('pages.groups.management.actions.edit')"
                icon="i-lucide-pencil"
                size="sm"
                color="neutral"
                :aria-label="t('pages.groups.management.actions.edit')"
                @click="editGroup(row.original)"
                variant="ghost"
              />
              <UButton
                v-app-tooltip="t('pages.groups.management.actions.delete')"
                icon="i-lucide-trash"
                size="sm"
                color="error"
                :aria-label="t('pages.groups.management.actions.delete')"
                @click="confirmDelete(row.original)"
                variant="ghost"
              />
            </div>
          </template>
        </AppDataTableShell>
      </template>
    </UCard>

    <GroupFormDialog
      :visible="showCreateDialog"
      :group="selectedGroup"
      @update:visible="updateDialogVisible"
      @success="onGroupSaved"
    />
  </div>
</template>

<style scoped>
.groups-page {
  padding: 1.5rem;
}
</style>
